import { BORDER, PRIMARY, SECONDARY, TITLE_GRADIENT } from "@app/theme/tokens";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDashboardPlants } from "../hooks/usePlantOverview";
import { NAV_ITEMS, PID_EXPLORER_PATH } from "./navItems";

export const RAIL_WIDTH = 232;

export default function NavRail() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const plants = useDashboardPlants().data ?? [];
  // The explorer falls back to the first plant when the URL carries none.
  const explorerPlantId = params.get("plant") ?? plants[0]?.id ?? null;

  return (
    <Box
      component="nav"
      sx={{
        width: RAIL_WIDTH,
        flexShrink: 0,
        bgcolor: "#fff",
        borderRight: `1px solid ${BORDER}`,
        color: "text.secondary",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* The plant sub-list of the explorer can outgrow the rail, so it scrolls on its own. */}
      <List
        disablePadding
        sx={{ pt: 1.5, flexGrow: 1, minHeight: 0, overflowY: "auto" }}
      >
        {NAV_ITEMS.map(({ label, icon: Icon, to }) => {
          const selected =
            to === "/dashboard" ? pathname === to : pathname.startsWith(to);
          return (
            <Box key={label}>
              <ListItemButton
                selected={selected}
                onClick={() => navigate(to)}
                sx={{
                  position: "relative",
                  minHeight: 46,
                  px: 2.5,
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1.5,
                  transition: "background-color 160ms, color 160ms",
                  "&:hover": { bgcolor: "#EDF1F7", color: SECONDARY },
                  "&.Mui-selected": {
                    bgcolor: `${PRIMARY}1A`,
                    color: PRIMARY,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 10,
                      bottom: 10,
                      width: 3,
                      borderRadius: 3,
                      background: TITLE_GRADIENT,
                    },
                  },
                  "&.Mui-selected:hover": { bgcolor: `${PRIMARY}26` },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: 2, color: "inherit" }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  sx={{
                    "& .MuiTypography-root": {
                      fontSize: 14,
                      fontWeight: selected ? 700 : 500,
                    },
                  }}
                />
              </ListItemButton>

              {to === PID_EXPLORER_PATH && selected && (
                <List disablePadding sx={{ mb: 1 }}>
                  {plants.map((plant) => (
                    <ListItemButton
                      key={plant.id}
                      selected={plant.id === explorerPlantId}
                      onClick={() =>
                        navigate(`${PID_EXPLORER_PATH}?plant=${plant.id}`)
                      }
                      sx={{
                        minHeight: 34,
                        pl: 7,
                        pr: 2,
                        mx: 1,
                        borderRadius: 1.5,
                        "&:hover": { bgcolor: "#EDF1F7", color: SECONDARY },
                        "&.Mui-selected": {
                          bgcolor: `${PRIMARY}14`,
                          color: PRIMARY,
                        },
                        "&.Mui-selected:hover": { bgcolor: `${PRIMARY}20` },
                      }}
                    >
                      <ListItemText
                        primary={plant.name}
                        sx={{
                          "& .MuiTypography-root": {
                            fontSize: 13,
                            fontWeight:
                              plant.id === explorerPlantId ? 700 : 500,
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          );
        })}
      </List>
      <Box
        sx={{
          px: 2.5,
          py: 2,
          flexShrink: 0,
          borderTop: `1px solid ${BORDER}`,
          fontSize: "0.62rem",
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#8E99A8",
        }}
      >
        Powered by ENGIE Laborelec
      </Box>
    </Box>
  );
}
